<template>
  <div>
    <KkLoading v-if="loading || notAvailable" />
    <div v-else>
      <template v-if="foodMenus.length === 0">
        <KkTutorial
          :description1="$t('pages.CompaniesFoodMenus.index.tutorial.noDataDescription1')"
          :description2="$t('pages.CompaniesFoodMenus.index.tutorial.noDataDescription2')"
          :img-path="tutorialImgPath"
          :tutorial-description="$t('pages.CompaniesFoodMenus.index.tutorial.description')"
          :available-service="$t(`pages.CompaniesFoodMenus.index.tutorial.${isDocomoPlan ? 'availableServiceDocomo' : 'availableService'}`)"
        >
          <template #buttonArea>
            <div class="c-button">
              <KkButton
                color="primary"
                :text="$t('pages.CompaniesFoodMenus.index.tutorial.register')"
                :to="{ name: 'CompaniesFoodMenusNew', params: { company_uuid: currentCompanyUuid }}"
              />
            </div>
          </template>
        </KkTutorial>
      </template>
      <template v-else>
        <div class="p-box">
          <div class="p-box__header">
            <div class="foodMenuItemWrapper">
              <KkButton
                v-if="isAdmin"
                height="32px"
                :font-bold="false"
                color="secondary"
                :text="$t('pages.CompaniesFoodMenus.index.tutorial.toCsv')"
                :to="{ name: 'CompaniesFoodMenusUploadedFiles', params: { company_uuid: currentCompanyUuid }}"
              />
              <router-link
                :to="{ name: 'CompaniesFoodMenusNew', params: { company_uuid: currentCompanyUuid }}"
                class="c-button__item c-button__item--primary"
                v-text="$t('pages.CompaniesFoodMenus.links.new')"
              />
            </div>
          </div>
          <div class="p-box__body">
            <KkInfo icon="hint">
              {{ $t('pages.CompaniesFoodMenus.index.info') }}
            </KkInfo>
            <div class="p-table__content">
              <table>
                <thead>
                  <tr>
                    <th>
                      {{ $t('pages.CompaniesFoodMenus.index.label') }}
                    </th>
                    <th>
                      {{ $t('pages.CompaniesFoodMenus.index.service') }}
                    </th>
                    <th>
                      {{ $t('pages.CompaniesFoodMenus.index.sections') }}
                    </th>
                    <th>
                      {{ $t('pages.CompaniesFoodMenus.index.updatedAt') }}
                      <span class="p-table__content__sort" :class="`is-${sortKey === 'updated_at' && sortOrder}`" @click="sort('updated_at')" />
                    </th>
                    <th>
                      {{ $t('pages.CompaniesFoodMenus.index.stores') }}
                      <span class="p-table__content__sort" :class="`is-${sortKey === 'store_count' && sortOrder}`" @click="sort('store_count')" />
                    </th>
                    <th />
                  </tr>
                  <tr class="p-table__content__space"><td colspan="5" /></tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(foodMenu, i) in foodMenus"
                    :key="i"
                    :class="{ 'progress': isProgress(foodMenu) }"
                  >
                    <td class="foodMenuLabel">
                      {{ foodMenu.label }}
                    </td>
                    <td>
                      <div class="serviceIconWrapper">
                        <KkSiteIcon v-for="(service, j) in foodMenu.targetServices"
                                    :key="`foodMenu-${foodMenu.id}-service-${j}`"
                                    :name="service"
                                    class="serviceIcon"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-for="(count, kind, index) in foodMenu.categories" :key="`foodMenu-${foodMenu.id}-category-${index}`">
                        <span>{{ $t(`pages.CompaniesFoodMenus.index.category.${kind}`) }}({{ count }})</span>
                      </div>
                    </td>
                    <td>
                      {{ basicDateFormat(foodMenu.updatedAt) }}
                    </td>
                    <td class="status">
                      <p>
                        {{ $t('pages.CompaniesFoodMenus.index.stores') }}:
                        <span :class="{ 'storeCount': foodMenu.stores.length }" @click="handleClickDisplayStoresModal(foodMenu)">
                          {{ $t('pages.CompaniesFoodMenus.index.storesCount', { count: foodMenu.stores.length }) }}
                        </span>
                      </p>
                      <div v-if="foodMenu.stores.length !== 0">
                        <StatusBadge :status="extractStatusNumber(foodMenu.status)" />
                        <span>
                          {{ displayStatus(foodMenu) }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div>
                        <router-link v-if="!isProgress(foodMenu)" :to="{ name: 'CompaniesFoodMenusEdit', params: { company_uuid: currentCompanyUuid, id: foodMenu.id }}" class="p-stores__action__item">
                          <KkIcon name="edit" color="rgba(58, 59, 63, 0.7)" class="p-stores__action__icon" />
                          {{ $t('labels.edit') }}
                        </router-link>
                        <router-link :to="{ name: 'CompaniesFoodMenusNew', params: { company_uuid: currentCompanyUuid }, query: { copy: foodMenu.id }}" class="p-stores__action__item">
                          <KkIcon name="copy" color="rgba(58, 59, 63, 0.7)" class="p-stores__action__icon" />
                          {{ $t('labels.copy') }}
                        </router-link>
                        <div
                          v-if="!isProgress(foodMenu)"
                          class="deleteButton p-stores__action__item"
                          @click="submitDeleteFoodMenu(foodMenu.id)"
                        >
                          <KkIcon name="delete" color="rgba(58, 59, 63, 0.7)" class="p-stores__action__icon" />
                          {{ $t('labels.delete') }}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="foodMenus.length !== 0" class="p-controller">
              <div class="p-controller__count">
                <p class="p-controller__count__name">
                  {{ $t('labels.perPageCount') }}
                </p>
                <div class="p-controller__count__select">
                  <select :value="displayNumber" @change="changeDisplayNumber($event)">
                    <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                  </select>
                </div>
                <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
              </div>
              <div class="p-controller__navi">
                <button
                  type="button"
                  :disabled="loading || currentPage === 1"
                  class="p-controller__navi__prev"
                  @click="goPrevPage"
                >
                  <KkIcon name="arrowLeft" color="rgba(58, 59, 63, 0.7)" />
                </button>
                <button
                  type="button"
                  :disabled="loading || currentPage >= meta.totalPages"
                  class="p-controller__navi__next"
                  @click="goNextPage"
                >
                  <KkIcon name="arrowRight" color="rgba(58, 59, 63, 0.7)" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <KkStoresModal
      v-if="showStoresModal"
      :is-visible="showStoresModal"
      :stores="stores"
      @close="closeStoresModal"
    />
  </div>
</template>

<script>
import {mapActions, mapState, mapGetters} from 'vuex'
import moment from 'moment'
import StatusBadge from '../../../components/StatusBadge'
import {
  KkButton,
  KkLoading,
  KkIcon,
  KkSiteIcon,
} from '../../../components/base'
import { KkInfo } from '../../../components/ui'
import { KkTutorial } from '../../../components/projects'
import tutorialImgPath from '../../../../../images/menus/tutorial.png'
import { KkStoresModal } from '../../../domains/foodMenu'

export default {
  name: 'CompaniesFoodMenusIndex',
  components: {
    StatusBadge,
    KkButton,
    KkLoading,
    KkIcon,
    KkInfo,
    KkTutorial,
    KkStoresModal,
    KkSiteIcon
  },
  data() {
    return {
      currentPage: 1,
      sortKey: 'updated_at',
      sortOrder: 'desc',
      showStoresModal: false,
      stores: null,
    }
  },
  computed: {
    ...mapState({
      foodMenus: state => state.companiesFoodMenus.data,
      meta: state => state.companiesFoodMenus.meta,
      notAvailable: state => state.companiesFoodMenus.notAvailable,
      foodMenusLoading: state => state.companiesFoodMenus.loading,
      displayNumber: state => state.filters.displayNumber,
      tutorialImgPath: () => tutorialImgPath
    }),
    ...mapGetters({
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
      isAdmin: 'companiesAccessibleApi/isAdmin',
    }),
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    startDataNumber() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber() {
      return this.startDataNumber + this.foodMenus.length - 1
    },
    loading() {
      return this.foodMenusLoading
    },
    query() {
      return {
        page: this.currentPage,
        per: this.displayNumber,
        sort_key: this.sortKey,
        sort_order: this.sortOrder
      }
    }
  },
  async created() {
    this.readFoodMenus(this.query)
  },
  methods: {
    ...mapActions({
      readFoodMenus: 'companiesFoodMenus/readFoodMenus',
      deleteFoodMenu: 'companiesFoodMenus/deleteFoodMenu',
      setDisplayNumber: 'filters/setDisplayNumber',
    }),
    calcErrorStoresCount(foodMenu) {
      return foodMenu.stores.filter(store => store.isError).length
    },
    displayStatus(foodMenu) {
      const count = foodMenu.status === 'error' ? this.calcErrorStoresCount(foodMenu) : undefined
      return this.$t(`pages.CompaniesFoodMenus.index.errors.${foodMenu.status}`, { count: count })
    },
    extractStatusNumber(status) {
      switch (status) {
      case 'complete':
        return 1
      case 'error':
        return 2
      case 'progress':
        return 3
      case 'pending':
        return 3
      default:
        return 3
      }
    },
    handleSubmit() {
      this.readFoodMenus(this.query)
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    sort(key) {
      if (this.loading) return
      if (this.sortKey === key) {
        this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
      this.handleResetPageAndSubmit()
    },
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
      this.handleResetPageAndSubmit()
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    basicDateFormat(date) {
      return date ? moment( date ).format(this.$t('format.date.default')) : ''
    },
    submitDeleteFoodMenu(id){
      if (confirm(this.$t('pages.CompaniesFoodMenus.index.deleteConfirm'))) {
        this.deleteFoodMenu(id)
      }
    },
    handleClickDisplayStoresModal(foodMenu) {
      if (foodMenu.stores.length === 0) return
      this.stores = foodMenu.stores
      this.showStoresModal = true
    },
    closeStoresModal() {
      this.showStoresModal = false
    },
    serviceIconPath(service) {
      switch (service) {
      case 'google':
        return iconGooglePath
      case 'tabelog':
        return iconTabelogPath
      }
    },
    isProgress(foodMenu) {
      return ['progress', 'pending'].includes(foodMenu.status) && foodMenu.stores.length !== 0
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';

.foodMenuItemWrapper {
  margin-left: auto;
  display: flex;
  gap: 10px
}

.info {
  margin-bottom: 24px !important;
}
.progress {
  background: #f8f9fa;
}
.p-table__content {
  table {
    border-collapse: separate;
    border-spacing: 0 12px;
  }
  tbody {
    td {
      background: unset;
      border-bottom: 1px solid #E4E7EB;
      &:not(:first-child) {
        border-left: none;
      }
      &:not(:last-child) {
        border-right: none;
      }
      &:first-child {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
      &:last-child {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }
}
.deleteButton {
  cursor: pointer;
}
.status {
  .storeCount {
    color: #E9446A;
    text-decoration: underline;
    cursor: pointer;
  }
}
.p-stores__action__icon {
  width: 16px;
}

.foodMenuLabel {
  font-weight: bold;
}
.serviceIconWrapper {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 4px;
}

.serviceIcon {
  width: 32px;
  height: 32px;
  border: solid 1px #E4E7EB;
  border-radius: 4px;
  padding: 4px;
  background-color: #fff;
}
</style>
